export type ThemeMaterial = 'glass' | 'solid' | 'translucent';

export interface ThemePreset {
  id: 'obsidian' | 'gold-vault' | 'caffe-latte' | 'marble-brass' | 'morning-greenhouse';
  name: string;
  description: string;
  material: ThemeMaterial;
  swatches: [string, string, string];
  accentIntensity: number;
  accentOverlay?: string;
}

export const THEMES: ThemePreset[] = [
  {
    id: 'obsidian',
    name: 'Obsidian Observatory',
    description: 'Dark glassmorphism default',
    material: 'glass',
    swatches: ['#0C0C0F', '#1A1A22', '#D4A017'],
    accentIntensity: 1.0,
  },
  {
    id: 'gold-vault',
    name: 'Gold Vault',
    description: 'Heavy glass with gold traces',
    material: 'glass',
    swatches: ['#0A0A14', '#141428', '#D4A017'],
    accentIntensity: 0.8,
    accentOverlay: '#D4A017',
  },
  {
    id: 'caffe-latte',
    name: 'Caffe Latte',
    description: 'Warm solid cards and soft shadows',
    material: 'solid',
    swatches: ['#FAF3E8', '#FFFFFF', '#8A7D6B'],
    accentIntensity: 0.7,
  },
  {
    id: 'marble-brass',
    name: 'Marble & Brass',
    description: 'Light premium with brass hairlines',
    material: 'glass',
    swatches: ['#FAFAFA', '#FFFFFF', '#B8860B'],
    accentIntensity: 0.9,
    accentOverlay: '#B8860B',
  },
  {
    id: 'morning-greenhouse',
    name: 'Morning Greenhouse',
    description: 'Fresh translucent dewy interface',
    material: 'translucent',
    swatches: ['#F2F8F0', '#FFFFFF', '#4A9D6E'],
    accentIntensity: 0.85,
  },
];

export function getThemeById(id: string): ThemePreset {
  return THEMES.find((theme) => theme.id === id) ?? THEMES[0]!;
}

function hexToRgb(hex: string): [number, number, number] {
  const value = hex.replace('#', '');
  if (value.length !== 6) return [255, 255, 255];
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
}

function clampChannel(value: number) {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function blend(base: [number, number, number], overlay: [number, number, number], ratio: number): [number, number, number] {
  const r = base[0] + (overlay[0] - base[0]) * ratio;
  const g = base[1] + (overlay[1] - base[1]) * ratio;
  const b = base[2] + (overlay[2] - base[2]) * ratio;
  return [clampChannel(r), clampChannel(g), clampChannel(b)];
}

export function getThemedAccent(baseAccentHex: string, theme: ThemePreset): string {
  const base = hexToRgb(baseAccentHex);
  const neutral: [number, number, number] = [140, 140, 140];
  const intensityMixed = blend(neutral, base, theme.accentIntensity);
  const overlay = theme.accentOverlay ? hexToRgb(theme.accentOverlay) : intensityMixed;
  const final = theme.accentOverlay ? blend(intensityMixed, overlay, 0.15) : intensityMixed;
  return `rgb(${final[0]} ${final[1]} ${final[2]})`;
}
